/**
 * CreateSubmissionModal
 *
 * Modal for creating a new lender submission for a credit application.
 * Fields: submissionType, submissionMethod, creditAnalystName,
 *         executiveSummary, coverLetter, submissionNotes.
 *
 * Creates via POST /api/applications/:applicationId/submissions
 * F006 AC002
 */

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const SUBMISSION_TYPE_OPTIONS = [
  { value: "initial", label: "Initial" },
  { value: "resubmission", label: "Resubmission" },
  { value: "variation", label: "Variation" },
  { value: "appeal", label: "Appeal" },
];

const SUBMISSION_METHOD_OPTIONS = [
  { value: "portal", label: "Portal" },
  { value: "email", label: "Email" },
  { value: "broker_platform", label: "Broker Platform" },
  { value: "api", label: "API" },
];

interface CreateSubmissionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: string;
}

export function CreateSubmissionModal({
  open,
  onOpenChange,
  applicationId,
}: CreateSubmissionModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [submissionType, setSubmissionType] = useState("initial");
  const [submissionMethod, setSubmissionMethod] = useState("portal");
  const [creditAnalystName, setCreditAnalystName] = useState("");
  const [executiveSummary, setExecutiveSummary] = useState("");
  const [coverLetter, setCoverLetter] = useState("");
  const [submissionNotes, setSubmissionNotes] = useState("");

  // Reset form when opening
  useEffect(() => {
    if (open) {
      setSubmissionType("initial");
      setSubmissionMethod("portal");
      setCreditAnalystName("");
      setExecutiveSummary("");
      setCoverLetter("");
      setSubmissionNotes("");
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", `/api/applications/${applicationId}/submissions`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/applications/${applicationId}/submissions`],
      });
      toast({ title: "Submission created" });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create submission",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    createMutation.mutate({
      submissionType,
      submissionMethod,
      creditAnalystName: creditAnalystName || null,
      executiveSummary: executiveSummary || null,
      coverLetter: coverLetter || null,
      submissionNotes: submissionNotes || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]" data-testid="dialog-create-submission">
        <DialogHeader>
          <DialogTitle>New Submission</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
          {/* Submission Type */}
          <div className="space-y-1.5">
            <Label>Submission Type</Label>
            <Select value={submissionType} onValueChange={setSubmissionType}>
              <SelectTrigger data-testid="select-submission-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {SUBMISSION_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Submission Method */}
          <div className="space-y-1.5">
            <Label>Submission Method</Label>
            <Select value={submissionMethod} onValueChange={setSubmissionMethod}>
              <SelectTrigger data-testid="select-submission-method">
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent>
                {SUBMISSION_METHOD_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Credit Analyst Name */}
          <div className="space-y-1.5">
            <Label>Credit Analyst Name</Label>
            <Input
              placeholder="e.g. John Smith"
              value={creditAnalystName}
              onChange={(e) => setCreditAnalystName(e.target.value)}
              data-testid="input-credit-analyst"
            />
          </div>

          {/* Executive Summary */}
          <div className="space-y-1.5">
            <Label>Executive Summary</Label>
            <Textarea
              placeholder="Brief summary of the submission..."
              value={executiveSummary}
              onChange={(e) => setExecutiveSummary(e.target.value)}
              rows={3}
              data-testid="input-executive-summary"
            />
          </div>

          {/* Cover Letter */}
          <div className="space-y-1.5">
            <Label>Cover Letter</Label>
            <Textarea
              placeholder="Cover letter content..."
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              rows={3}
              data-testid="input-cover-letter"
            />
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea
              placeholder="Additional notes..."
              value={submissionNotes}
              onChange={(e) => setSubmissionNotes(e.target.value)}
              rows={2}
              data-testid="input-submission-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
            data-testid="button-cancel-submission"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            data-testid="button-submit-submission"
          >
            {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Submission
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
